import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBriefcase, FiMail, FiSend, FiCopy, FiCheck, FiPlus, FiEye, FiTrash2 } = FiIcons;

const HostCompanies = () => {
  const { companies, addCompany } = useData();
  const { user } = useAuth();
  const { addPoints } = useGamification();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPerson: ''
  });
  const [generatedCode, setGeneratedCode] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  // Filter companies for this host
  const myCompanies = companies.filter(c => c.hostId === user.id || !c.hostId);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generateRegistrationCode = () => {
    return 'JF-' + Math.random().toString(36).substr(2, 8).toUpperCase();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = generateRegistrationCode();
    
    // Add company with registration code
    addCompany({
      ...formData,
      hostId: user.id,
      registrationCode: code,
      status: 'invited',
      invitedAt: new Date().toISOString()
    });
    
    // Award points to Host for inviting company
    addPoints('hostCompanyInvited');
    
    setGeneratedCode(code);
    
    // Simulate sending email
    setTimeout(() => {
      alert(`Registration code sent to ${formData.contactEmail}`);
      setFormData({ name: '', contactEmail: '', contactPerson: '' });
      setShowForm(false);
      setGeneratedCode('');
    }, 1000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const resendInvite = (company) => {
    alert(`Invitation resent to ${company.contactEmail}`);
  };

  const deleteCompany = (companyId) => {
    if (window.confirm('Are you sure you want to remove this company?')) {
      console.log('Deleting company:', companyId);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Management</h1>
            <p className="text-gray-600">Invite companies to your event and manage registration codes.</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Invite Company</span>
          </button>
        </div>
      </motion.div>

      {/* Company Invitation Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Invite Company</h2>
          {!generatedCode ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiBriefcase} className="text-gray-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    id="contactPerson"
                    name="contactPerson"
                    type="text"
                    required
                    value={formData.contactPerson}
                    onChange={handleChange}
                    className="block w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact person name"
                  />
                </div>
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="text-gray-400" />
                    </div>
                    <input
                      id="contactEmail"
                      name="contactEmail"
                      type="email"
                      required
                      value={formData.contactEmail}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contact@company.com"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• A unique registration code will be generated</li>
                  <li>• An invitation email will be sent to the contact person</li>
                  <li>• The company can register using this code</li>
                  <li className="font-semibold mt-2">+50 points for every invite!</li>
                </ul>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
                >
                  <SafeIcon icon={FiSend} />
                  <span>Generate Code & Send Invite</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <SafeIcon icon={FiCheck} className="text-green-600 text-4xl mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-green-900 mb-2">Registration Code Generated</h3>
                <p className="text-green-700 mb-4">
                  The following registration code has been generated for {formData.name}:
                </p>
                <div className="bg-white p-4 rounded-lg border border-green-200 mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <code className="text-xl font-mono font-bold text-gray-900">{generatedCode}</code>
                    <button
                      onClick={() => copyToClipboard(generatedCode)}
                      className="text-green-600 hover:text-green-700 transition-colors"
                    >
                      <SafeIcon icon={copiedCode ? FiCheck : FiCopy} />
                    </button>
                  </div>
                  {copiedCode && (
                    <p className="text-sm text-green-600 mt-2">Copied to clipboard!</p>
                  )}
                </div>
                <p className="text-sm text-green-600">
                  An invitation email is being sent to {formData.contactEmail}
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Companies List */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Invited Companies</h2>
        </div>
        <div className="p-6">
          {myCompanies.length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiBriefcase} className="text-gray-400 text-4xl mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No companies invited yet</h3>
              <p className="text-gray-600 mb-6">Start by inviting your first company to the job fair.</p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} />
                <span>Invite First Company</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {myCompanies.map((company, index) => (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={FiBriefcase} className="text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">{company.name}</h3>
                        <p className="text-sm text-gray-600">{company.contactEmail}</p>
                        {company.registrationCode && (
                          <div className="flex items-center space-x-2 mt-1">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {company.registrationCode}
                            </code>
                            <button
                              onClick={() => copyToClipboard(company.registrationCode)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <SafeIcon icon={FiCopy} className="text-xs" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        company.status === 'registered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {company.status === 'registered' ? 'Registered' : 'Invited'}
                      </span>
                      <div className="flex space-x-1">
                        <button onClick={() => resendInvite(company)} className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors text-sm" title="Resend invite">
                          <SafeIcon icon={FiSend} />
                        </button>
                        <button className="bg-gray-600 text-white p-2 rounded-md hover:bg-gray-700 transition-colors text-sm" title="View details">
                          <SafeIcon icon={FiEye} />
                        </button>
                        <button onClick={() => deleteCompany(company.id)} className="bg-red-600 text-white p-2 rounded-md hover:bg-red-700 transition-colors text-sm" title="Remove company">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default HostCompanies;